// frontend/src/components/layouts/Header.js - Updated with Egg Category
import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
    Menu,
    X,
    ShoppingCart,
    User,
    LogOut,
    Package,
    Home,
    ShoppingBag,
    HelpCircle,
    Phone,
    LayoutDashboard,
    CheckCircle,
    Info,
    Mail,
    ChevronDown,
    Drumstick,
    Beef,
    Egg
} from 'lucide-react';
import { toast } from 'react-toastify';
import { logout } from '../../actions/userActions';
import { logoutAdmin } from '../../actions/adminActions';
import { logoutDeliveryStaff } from '../../actions/deliveryStaffActions';
import Search from './Search';

const Header = () => {
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const [isProfileDropdownOpen, setIsProfileDropdownOpen] = useState(false);
    const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
    const [topBarHeight, setTopBarHeight] = useState(0);
    const [isMobile, setIsMobile] = useState(false);
    const [headerHeight, setHeaderHeight] = useState(0);
    const [searchBarHeight, setSearchBarHeight] = useState(0);
    const topBarRef = useRef(null);
    const headerRef = useRef(null);
    const searchBarRef = useRef(null);
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const { items: cartItems } = useSelector(state => state.cartState);
    const { user, isAuthenticated } = useSelector(state => state.authState);
    const { admin, isAuthenticated: isAdminAuthenticated } = useSelector(state => state.adminState);
    const { deliveryStaff, isAuthenticated: isDeliveryAuthenticated } = useSelector(state => state.deliveryStaffState);
   
    const categoryRef = useRef(null);
    const backgroundImage = "https://meatbuddy-assets.s3.eu-north-1.amazonaws.com/other-images/meatbuddy-image-web-banner-background-5.png";
   
    // Check if current path is admin or delivery staff route
    const isAdminOrDeliveryRoute = location.pathname.includes('/admin') || location.pathname.includes('/delivery');
   
    // Updated category structure - added Egg
    const categoryStructure = {
        'Mutton': [
            'Mutton - Fresh Cuts',
            'Mutton - Premium Cuts'
        ],
        'Chicken': [
            'Chicken - Fresh Cuts',
            'Chicken - Special Cuts'
        ],
        'Egg': [
            'Egg -- Fresh Egg'
        ]
    };
    
    // Check if we're on mobile view
    useEffect(() => {
        const checkMobile = () => {
            setIsMobile(window.innerWidth < 768);
        };
       
        checkMobile();
        window.addEventListener('resize', checkMobile);
       
        return () => {
            window.removeEventListener('resize', checkMobile);
        };
    }, []);
    
    // Calculate heights for proper positioning and spacing
    useEffect(() => {
        if (topBarRef.current && !isAdminOrDeliveryRoute) {
            const height = topBarRef.current.getBoundingClientRect().height;
            setTopBarHeight(height);
        }
       
        if (headerRef.current) {
            const height = headerRef.current.getBoundingClientRect().height;
            setHeaderHeight(height);
        }
       
        if (isMobile && searchBarRef.current) {
            const height = searchBarRef.current.getBoundingClientRect().height;
            setSearchBarHeight(height);
        }
    }, [isAdminOrDeliveryRoute, isMobile]);

    const toggleDrawer = (event) => {
        event.stopPropagation();
        setIsDrawerOpen(!isDrawerOpen);
    };

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (isDrawerOpen && !event.target.closest('.mb-drawer') && !event.target.closest('.toggle-button')) {
                setIsDrawerOpen(false);
            }
           
            if (showCategoryDropdown && categoryRef.current && !categoryRef.current.contains(event.target)) {
                setShowCategoryDropdown(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isDrawerOpen, showCategoryDropdown]);

    const getCurrentUser = () => {
        if (isAdminAuthenticated) return { ...admin, role: 'admin' };
        if (isDeliveryAuthenticated) return { ...deliveryStaff, role: 'delivery' };
        if (isAuthenticated) return { ...user, role: 'user' };
        return null;
    };

    const currentUser = getCurrentUser();

    const navigationItems = [
        { label: 'Home', path: '/', icon: <Home className="h-5 w-5" /> },
        { label: 'Shop', path: '/shop', icon: <ShoppingBag className="h-5 w-5" /> },
        { label: 'About Us', path: '/about', icon: <Info className="h-5 w-5" /> },
        { label: 'FAQ', path: '/faq', icon: <HelpCircle className="h-5 w-5" /> },
        { label: 'Contact Us', path: '/contact', icon: <Phone className="h-5 w-5" /> }
    ];

    const getProfileMenuItems = (role) => {
        switch (role) {
            case 'admin':
                return [
                    { label: 'Dashboard', path: '/admin/dashboard', icon: <LayoutDashboard className="h-4 w-4 mr-2" /> },
                    { label: 'Profile', path: '/admin/profile', icon: <User className="h-4 w-4 mr-2" /> }
                ];
            case 'delivery':
                return [
                    { label: 'Dashboard', path: '/delivery/dashboard', icon: <Package className="h-4 w-4 mr-2" /> },
                    { label: 'Profile', path: '/delivery/profile', icon: <User className="h-4 w-4 mr-2" /> },
                    { label: 'Delivered Orders', path: '/delivery/orders/delivered', icon: <CheckCircle className="h-4 w-4 mr-2" /> }
                ];
            default:
                return [
                    { label: 'Profile', path: '/myprofile', icon: <User className="h-4 w-4 mr-2" /> },
                    { label: 'Orders', path: '/orders', icon: <Package className="h-4 w-4 mr-2" /> }
                ];
        }
    };

    const handleCategoryClick = (parentCategory) => {
        navigate(`/shop?category=${encodeURIComponent(parentCategory)}`);
        setShowCategoryDropdown(false);
    };

    const handleLogout = () => {
        if (isAdminAuthenticated) {
            dispatch(logoutAdmin());
            navigate('/admin/login');
        } else if (isDeliveryAuthenticated) {
            dispatch(logoutDeliveryStaff());
            navigate('/delivery/login');
        } else {
            dispatch(logout());
            navigate('/login');
        }
        setIsDrawerOpen(false);
        setIsProfileDropdownOpen(false);
        toast.success('Logged out successfully');
    };

    const getTotalHeaderHeight = () => {
        if (isAdminOrDeliveryRoute) {
            return isMobile ? '64px' : '80px';
        } else {
            if (isMobile) {
                return '112px';
            } else {
                return `calc(${topBarHeight}px + 80px)`;
            }
        }
    };

    // COMPLETELY REWRITTEN drawer function to avoid CSS conflicts
    const renderDrawer = () => {
        // Add a style tag directly to ensure highest priority styling
        const drawerStyles = `
            #mb-custom-drawer {
                background-image: url(${backgroundImage});
                background-repeat: repeat;
                background-size: auto;
                background-position: center;
                background-color: transparent !important;
            }
           
            #mb-custom-drawer * {
                background-color: transparent;
            }
           
            #mb-custom-drawer .mb-menu-item {
                background-color: rgba(255, 255, 255, 0.7);
                color: #333;
                display: flex;
                align-items: center;
                padding: 12px 16px;
                margin-bottom: 8px;
                border-radius: 8px;
                text-decoration: none;
                transition: all 0.2s ease;
            }
           
            #mb-custom-drawer .mb-menu-item:hover {
                background-color: white;
                color: #dc2626;
            }
           
            #mb-custom-drawer .mb-menu-item-icon {
                margin-right: 12px;
            }
        `;

        return (
            <>
                {/* Inject styles directly */}
                <style>{drawerStyles}</style>
               
                {/* Drawer with unique ID and classes to avoid conflicts */}
                <div
                    id="mb-custom-drawer"
                    className="mb-drawer fixed inset-y-0 left-0 w-64 z-50 shadow-lg"
                >
                    <div className="h-full flex flex-col">
                        <div className="p-4 flex justify-between items-center border-b border-gray-200 bg-white bg-opacity-90">
                            <h2 className="text-xl font-bold text-[#dc2626] mt-10">Menu</h2>
                            <button
                                onClick={toggleDrawer}
                                className="text-[#dc2626] hover:text-[#dc2626]/80 transition-colors"
                            >
                                <X className="h-6 w-6" />
                            </button>
                        </div>
                       
                        <div className="mb-menu-container p-4 flex-1 overflow-y-auto">
                            {navigationItems.map(item => (
                                <Link
                                    key={item.path}
                                    to={item.path}
                                    className="mb-menu-item"
                                    onClick={toggleDrawer}
                                >
                                    <span className="mb-menu-item-icon">{item.icon}</span>
                                    <span>{item.label}</span>
                                </Link>
                            ))}
                        </div>
                    </div>
                </div>
            </>
        );
    };

    if (!isAdminOrDeliveryRoute) {
        return (
            <>
                {!isMobile && (
                    <div
                        ref={topBarRef}
                        className="bg-[#dc2626] text-white py-2 fixed top-0 left-0 right-0 z-50"
                    >
                        <div className="container mx-auto px-4 flex justify-between items-center">
                            <div className="flex items-center">
                                <Phone className="h-4 w-4 mr-2" />
                                <a href="tel:+916364749404" className="text-white hover:text-gray-200 transition-colors no-underline">
                                    +91 6364749404
                                </a>
                            </div>
                            <div className="flex items-center">
                                <Mail className="h-4 w-4 mr-2" />
                                <a href="mailto:customersupport@meatbuddy.in" className="text-white hover:text-gray-200 transition-colors no-underline">
                                    customersupport@meatbuddy.in
                                </a>
                            </div>
                        </div>
                    </div>
                )}
               
                <header
                    ref={headerRef}
                    className="bg-white fixed left-0 right-0 z-40 shadow-md"
                    style={{
                        backgroundImage: `url(${backgroundImage})`,
                        backgroundRepeat: 'repeat',
                        backgroundSize: 'auto',
                        backgroundPosition: 'center',
                        top: !isMobile ? `${topBarHeight}px` : '0'
                    }}
                >
                    <div className="container mx-auto px-4">
                        <div className="flex items-center justify-between h-16 md:h-20">
                            <div className="flex items-center">
                                {/* Only show hamburger menu when drawer is closed */}
                                {!isDrawerOpen && (
                                    <button
                                        onClick={toggleDrawer}
                                        className="text-[#dc2626] hover:text-[#dc2626]/80 transition-colors p-2 rounded-lg focus:outline-none toggle-button"
                                        style={{ zIndex: 60, pointerEvents: 'auto' }}
                                    >
                                        <Menu className="h-5 w-5 md:h-6 md:w-6" />
                                    </button>
                                )}
                                <Link
                                    to="/"
                                    className="no-underline flex items-center"
                                    style={{ marginLeft: isDrawerOpen ? '0' : '-8px' }}
                                >
                                    <img
                                        src="/images/logo-meatbuddy-bengaluru.png"
                                        alt="Meat Buddy"
                                        className="h-24 w-40 md:h-32 md:w-64 object-contain hover:opacity-90 transition-opacity md:mt-4 mt-4"
                                    />
                                </Link>
                            </div>
                           
                            <div className="flex-1 max-w-md mx-4 hidden md:block">
                                <div className="relative">
                                    <Search />
                                </div>
                            </div>
                           
                            <div
                                ref={categoryRef}
                                className="hidden lg:block relative"
                            >
                                <button
                                    onClick={() => setShowCategoryDropdown(!showCategoryDropdown)}
                                    onMouseEnter={() => setShowCategoryDropdown(true)}
                                    className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:text-[#dc2626] transition-colors"
                                >
                                    Categories
                                    <ChevronDown className="w-4 h-4" />
                                </button>
                                {showCategoryDropdown && (
                                    <div
                                        className="absolute top-full left-0 bg-white shadow-lg rounded-lg py-2 w-64 z-50"
                                        onMouseLeave={() => setShowCategoryDropdown(false)}
                                    >
                                        {Object.keys(categoryStructure).map((category) => (
                                            <div
                                                key={category}
                                                className="w-full flex items-center gap-2 px-4 py-2 hover:bg-gray-50 text-gray-700 hover:text-[#dc2626] cursor-pointer"
                                                onClick={() => handleCategoryClick(category)}
                                            >
                                                {category === 'Mutton' ? (
                                                    <Beef className="w-5 h-5" />
                                                ) : category === 'Chicken' ? (
                                                    <Drumstick className="w-5 h-5" />
                                                ) : (
                                                    <Egg className="w-5 h-5" />
                                                )}
                                                <span className="font-medium text-sm">{category}</span>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                           
                            <div className="flex items-center justify-end space-x-3 md:space-x-6">
                                {currentUser ? (
                                    <div className="relative">
                                        <button
                                            onClick={() => setIsProfileDropdownOpen(!isProfileDropdownOpen)}
                                            className="flex items-center space-x-1 md:space-x-2 text-gray-700"
                                        >
                                            <div className="w-7 h-7 md:w-8 md:h-8 rounded-full bg-gray-200 overflow-hidden shrink-0">
                                                {currentUser.avatar || (currentUser.profilePicture && currentUser.role === 'delivery') ? (
                                                    <img
                                                        src={currentUser.role === 'delivery' ? currentUser.profilePicture : currentUser.avatar}
                                                        alt="Profile"
                                                        className="w-full h-full object-cover"
                                                        onError={(e) => {
                                                            console.error("Image failed to load");
                                                            e.target.onerror = null;
                                                            e.target.src = "/images/default_avatar.png";
                                                        }}
                                                    />
                                                ) : (
                                                    <User className="w-full h-full text-gray-400 p-1" />
                                                )}
                                            </div>
                                            <span className="text-xs whitespace-nowrap overflow-hidden text-ellipsis max-w-[60px] md:max-w-[100px]">
                                                {currentUser.name}
                                            </span>
                                        </button>
                                        {isProfileDropdownOpen && (
                                            <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg py-1 z-50">
                                                {getProfileMenuItems(currentUser.role).map((item) => (
                                                    <Link
                                                        key={item.path}
                                                        to={item.path}
                                                        className="flex items-center px-4 py-2 text-gray-800 hover:text-[#dc2626] hover:bg-gray-50 no-underline text-xs"
                                                        onClick={() => setIsProfileDropdownOpen(false)}
                                                    >
                                                        {item.icon}
                                                        {item.label}
                                                    </Link>
                                                ))}
                                                <button
                                                    onClick={handleLogout}
                                                    className="flex items-center w-full px-4 py-2 text-left text-[#dc2626] hover:bg-gray-50 text-xs"
                                                >
                                                    <LogOut className="h-4 w-4 mr-2" />
                                                    Logout
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                ) : (
                                    <Link
                                        to="/login"
                                        className="px-3 py-1.5 md:px-6 md:py-2 bg-[#dc2626] text-white rounded-lg hover:bg-[#dc2626]/90 transition-colors font-medium no-underline text-xs"
                                    >
                                        Login
                                    </Link>
                                )}
                               
                                <Link to="/cart" className="relative no-underline">
                                    <ShoppingCart className="h-5 w-5 md:h-6 md:w-6 text-gray-800 hover:text-[#dc2626] transition-colors" />
                                    {cartItems.length > 0 && (
                                        <span className="absolute -top-2 -right-2 bg-[#dc2626] text-white text-xs rounded-full h-4 w-4 md:h-5 md:w-5 flex items-center justify-center">
                                            {cartItems.length}
                                        </span>
                                    )}
                                </Link>
                            </div>
                        </div>
                       
                        <div ref={searchBarRef} className="md:hidden px-2 pb-2 pt-1">
                            <Search />
                        </div>
                    </div>
                   
                    {isDrawerOpen && renderDrawer()}
                </header>
                <div
                    className="header-spacer"
                    style={{
                        height: getTotalHeaderHeight()
                    }}
                ></div>
            </>
        );
    }
   
    // For admin or delivery staff routes, only show the profile section in header
    return (
        <>
            <header
                ref={headerRef}
                className="bg-white fixed top-0 left-0 right-0 z-50 shadow-md"
                style={{
                    backgroundImage: `url(${backgroundImage})`,
                    backgroundRepeat: 'repeat',
                    backgroundSize: 'auto',
                    backgroundPosition: 'center'
                }}
            >
                <div className="container mx-auto px-4">
                    <div className="flex items-center justify-between h-16 md:h-20">
                        {/* Left side - Keep empty space where logo would be */}
                        <div className="flex-1"></div>
                       
                        {/* Right side - Only show profile section */}
                        <div className="flex items-center justify-end">
                            {currentUser && (
                                <div className="relative">
                                    <button
                                        onClick={() => setIsProfileDropdownOpen(!isProfileDropdownOpen)}
                                        className="flex items-center space-x-1 md:space-x-2 text-gray-700"
                                    >
                                        <div className="w-7 h-7 md:w-8 md:h-8 rounded-full bg-gray-200 overflow-hidden shrink-0">
                                            {currentUser.avatar || (currentUser.profilePicture && currentUser.role === 'delivery') ? (
                                                <img
                                                    src={currentUser.role === 'delivery' ? currentUser.profilePicture : currentUser.avatar}
                                                    alt="Profile"
                                                    className="w-full h-full object-cover"
                                                    onError={(e) => {
                                                        console.error("Image failed to load");
                                                        e.target.onerror = null;
                                                        e.target.src = "/images/default_avatar.png";
                                                    }}
                                                />
                                            ) : (
                                                <User className="w-full h-full text-gray-400 p-1" />
                                            )}
                                        </div>
                                        <span className="text-xs whitespace-nowrap overflow-hidden text-ellipsis max-w-[60px] md:max-w-[100px]">
                                            {currentUser.name}
                                        </span>
                                    </button>
                                    {isProfileDropdownOpen && (
                                        <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg py-1 z-50">
                                            {getProfileMenuItems(currentUser.role).map((item) => (
                                                <Link
                                                    key={item.path}
                                                    to={item.path}
                                                    className="flex items-center px-4 py-2 text-gray-800 hover:text-[#dc2626] hover:bg-gray-50 no-underline text-xs"
                                                    onClick={() => setIsProfileDropdownOpen(false)}
                                                >
                                                    {item.icon}
                                                    {item.label}
                                                </Link>
                                            ))}
                                            <button
                                                onClick={handleLogout}
                                                className="flex items-center w-full px-4 py-2 text-left text-[#dc2626] hover:bg-gray-50 text-xs"
                                            >
                                                <LogOut className="h-4 w-4 mr-2" />
                                                Logout
                                            </button>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
               
                {isDrawerOpen && renderDrawer()}
            </header>
            <div
                className="header-spacer"
                style={{
                    height: getTotalHeaderHeight()
                }}
            ></div>
        </>
    );
};

export default Header;